import React, { useState, useMemo } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material"
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  PowerSettingsNew as PowerIcon,
  Clear as ClearIcon,
} from "@mui/icons-material"
import { deleteAiAgent, toggleAiAgentStatus } from "services/aiAgent"
import { listAiVoices } from "services/aiVoices"
import { addFromVoice } from "services/aiAgent"
import { verifyResponse } from "utils/httpRequest"

const AiAgentTable = ({
  aiAgents,
  access,
  tierPrograms = [],
  products = [],
  onEdit,
  onDelete,
  onToggleStatus,
  onViewDetail,
}) => {
  const [searchForm, setSearchForm] = useState({
    keyword: "",
    isActive: "",
    specialty: "",
  })
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [agentToDelete, setAgentToDelete] = useState(null)
  const [matchingVoices, setMatchingVoices] = useState([])

  // Client-side filtering
  const filteredAgents = useMemo(() => {
    return aiAgents.filter((agent) => {
      const matchesKeyword = !searchForm.keyword || 
        agent.name?.toLowerCase().includes(searchForm.keyword.toLowerCase()) ||
        agent.title?.toLowerCase().includes(searchForm.keyword.toLowerCase()) ||
        agent.shortDescription?.toLowerCase().includes(searchForm.keyword.toLowerCase())
      
      const matchesStatus = !searchForm.isActive || 
        (searchForm.isActive === "1" && agent.isActive) ||
        (searchForm.isActive === "0" && !agent.isActive)
      
      const matchesSpecialty = !searchForm.specialty || 
        agent.specialties?.some(s => s.toLowerCase().includes(searchForm.specialty.toLowerCase()))
      
      return matchesKeyword && matchesStatus && matchesSpecialty
    })
  }, [aiAgents, searchForm])

  const handleSearchChange = (field, value) => {
    setSearchForm({ ...searchForm, [field]: value })
  }

  const handleClearSearch = () => {
    setSearchForm({
      keyword: "",
      isActive: "",
      specialty: "",
    })
    setMatchingVoices([])
  }

  // Load matching voices when keyword changes
  React.useEffect(() => {
    let mounted = true
    const loadVoices = async () => {
      if (!searchForm.keyword || searchForm.keyword.length < 2) {
        if (mounted) setMatchingVoices([])
        return
      }
      try {
        const resp = await listAiVoices({ page: 1, pageSize: 500 })
        const voices = resp?.data?.list || resp?.list || resp || []
        const kw = searchForm.keyword.toLowerCase()
        const matches = (voices || []).filter((v) => {
          return (
            (v.name || "").toLowerCase().includes(kw) ||
            (v.provider_voice_id || "").toLowerCase().includes(kw) ||
            (v.provider || "").toLowerCase().includes(kw)
          )
        })
        if (mounted) setMatchingVoices(matches.slice(0, 6))
      } catch (err) {
        console.error('Failed to load voices', err)
        if (mounted) setMatchingVoices([])
      }
    }
    loadVoices()
    return () => { mounted = false }
  }, [searchForm.keyword])

  const handleDeleteClick = (agent) => {
    setAgentToDelete(agent)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (agentToDelete) {
      try {
        const response = await deleteAiAgent(agentToDelete.id)
        verifyResponse(response)
        onDelete()
        setDeleteDialogOpen(false)
        setAgentToDelete(null)
      } catch (error) {
        console.error("Error deleting AI agent:", error)
      }
    }
  }

  const handleToggleStatus = async (agent) => {
    try {
      const response = await toggleAiAgentStatus(agent.id)
      verifyResponse(response)
      onToggleStatus()
    } catch (error) {
      console.error("Error toggling AI agent status:", error)
    }
  }

  const formatTierAccess = (agent) => {
    // Prefer direct fields if present
    const tierTypeRaw =
      agent?.tierProgramType ??
      agent?.tierProgram?.type ??
      null
    const tierProgramId =
      agent?.tierProgramId ??
      (agent?.tierProgram ? agent.tierProgram.id : null) ??
      null

    const tierTypeFromLookup = tierProgramId
      ? (Array.isArray(tierPrograms)
          ? tierPrograms.find((t) => Number(t?.id) === Number(tierProgramId))?.type
          : null)
      : null

    const tierType = String(tierTypeRaw || tierTypeFromLookup || "").trim()
    if (!tierType) return "-"

    if (tierType.toLowerCase() === "product") {
      const productProgramId = agent?.productProgramId ?? null
      if (!productProgramId) return "Product"
      const product = Array.isArray(products)
        ? products.find((p) => Number(p?.programId) === Number(productProgramId))
        : null
      const label = product?.name || product?.code || `#${productProgramId}`
      return `Product: ${label}`
    }

    return tierType.charAt(0).toUpperCase() + tierType.slice(1)
  }

  return (
    <div>
      <div className="search-section">
        <div className="search-form">
          <TextField
            className="search-keyword"
            size="small"
            placeholder="Search by name, title, or description..."
            value={searchForm.keyword}
            onChange={(e) => handleSearchChange("keyword", e.target.value)}
          />
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={searchForm.isActive}
              onChange={(e) => handleSearchChange("isActive", e.target.value)}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="1">Active</MenuItem>
              <MenuItem value="0">Inactive</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            size="small"
            placeholder="Filter by specialty..."
            value={searchForm.specialty}
            onChange={(e) => handleSearchChange("specialty", e.target.value)}
            sx={{ minWidth: 150 }}
          />
          
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleClearSearch}
            size="small"
          >
            Clear
          </Button>
        </div>
      </div>

      <div className="table-container">
        {matchingVoices.length > 0 && (
          <div className="matching-voices">
            <p className="matching-voices-title">Matching Voices</p>
            <div className="matching-voices-list">
              {matchingVoices.map((mv) => {
                const exists = aiAgents.some((a) => String(a.name).toLowerCase() === String(mv.name || '').toLowerCase())
                return (
                  <div key={mv.id} className="matching-voice-item">
                    <Typography>{mv.name || mv.provider_voice_id || mv.provider}</Typography>
                    {!exists ? (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={async () => {
                          try {
                            await addFromVoice(mv.id)
                            onDelete()
                          } catch {}
                        }}
                      >
                        Add
                      </Button>
                    ) : (
                      <Button size="small" disabled>
                        Added
                      </Button>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Short Description</TableCell>
                <TableCell>Specialties</TableCell>
                <TableCell>Tier Access</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Sort Order</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAgents.length === 0 && (
                <TableRow>
                  <TableCell className="table-empty" colSpan={8}>
                    {aiAgents.length === 0
                      ? "No AI agents found."
                      : "No agents match your search criteria."}
                  </TableCell>
                </TableRow>
              )}
              {filteredAgents.map((agent) => (
                <TableRow key={agent.id}>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {agent.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{agent.title}</TableCell>
                  <TableCell className="description-cell">
                    {agent.shortDescription}
                  </TableCell>
                  <TableCell>
                    <div className="specialties-tags">
                      {agent.specialties?.map((specialty, index) => (
                        <Chip
                          key={index}
                          label={specialty}
                          size="small"
                          className="specialty-tag"
                        />
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>{formatTierAccess(agent)}</TableCell>
                  <TableCell>
                    <Chip
                      label={agent.isActive ? "Active" : "Inactive"}
                      className={`status-badge ${
                        agent.isActive ? "status-active" : "status-inactive"
                      }`}
                    />
                  </TableCell>
                  <TableCell>{agent.sortOrder}</TableCell>
                  <TableCell>
                    <div className="action-buttons">
                      {access.getDetailAiAgentAccess && (
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => onViewDetail(agent)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {access.editAiAgentAccess && (
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => onEdit(agent)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {access.toggleStatusAiAgentAccess && (
                        <Tooltip title="Toggle Status">
                          <IconButton
                            size="small"
                            onClick={() => handleToggleStatus(agent)}
                          >
                            <PowerIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {access.deleteAiAgentAccess && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(agent)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            width: "min(520px, calc(100vw - 28px))",
            borderRadius: "16px",
            border: "1px solid var(--cms-border)",
            background: "var(--cms-dialog-bg)",
            boxShadow: "0 24px 48px rgba(15, 23, 42, 0.2)",
          },
        }}
      >
        <DialogTitle
          sx={{ pb: 1, fontWeight: 800, color: "var(--cms-text-primary)", letterSpacing: "-0.01em" }}
        >
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the AI Agent "{agentToDelete?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, pt: 1, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ textTransform: "none", borderRadius: "9px", fontWeight: 700 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            sx={{ textTransform: "none", borderRadius: "9px", fontWeight: 700 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default AiAgentTable
